import './BaziCard.css';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { getGanWuxing, getZhiWuxing, getWuxingColor } from '../utils/wuxing-calculator';

const PILLAR_LABELS = ['年', '月', '日', '时'];

// 四柱迷你盘：天干/地支竖排，按五行色着色（不再做成按钮样子）
const MiniChart = ({ baziResult }) => {
  const pillars = [
    baziResult?.yearPillar,
    baziResult?.monthPillar,
    baziResult?.dayPillar,
    baziResult?.hourPillar
  ];
  return (
    <div className="bazi-card-chart">
      {pillars.map((p, i) => (
        <div className="bcc-col" key={i}>
          <span className="bcc-label">{PILLAR_LABELS[i]}</span>
          <span className="bcc-char" style={{ color: getWuxingColor(getGanWuxing(p?.gan)) }}>{p?.gan || '—'}</span>
          <span className="bcc-char" style={{ color: getWuxingColor(getZhiWuxing(p?.zhi)) }}>{p?.zhi || '—'}</span>
        </div>
      ))}
    </div>
  );
};

/**
 * 命例卡（Dashboard + 社区共用）。
 * - record：八字记录（含 name/gender/baziResult/gregorianDate/labels）
 * - onOpen(id)：点整卡进入详情
 * - actions：右上角「⋯」菜单项 [{ key, label, icon, danger, onClick }]
 * - publisher：社区卡右上角发布者小字（可选）
 */
const BaziCard = ({ record, onOpen, actions = [], publisher }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const close = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen, close]);

  const formatDate = (d) => {
    if (!d || !d.year) return '生辰未知';
    const hm = (d.hour != null) ? ` ${d.hour}时${d.minute || 0}分` : '';
    return `${d.year}年${d.month}月${d.day}日${hm}`;
  };

  // 仅在标签被 populate（对象带 name）时展示 chips；Dashboard 列表未 populate 则不显示
  const tags = (record.labels || [])
    .map((l) => (l && typeof l === 'object' ? l.name : null))
    .filter(Boolean);
  const shownTags = tags.slice(0, 3);
  const extraTags = tags.length - shownTags.length;

  const genderCls = record.gender === '女' ? 'female' : 'male';

  return (
    <div
      className="bazi-card"
      role="button"
      tabIndex={0}
      onClick={() => onOpen && onOpen(record._id)}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen && onOpen(record._id); }}
    >
      <div className="bazi-card-head">
        <div className="bazi-card-title">
          <h3 className="bazi-card-name">{record.name || '无名'}</h3>
          <span className={`bazi-card-gender ${genderCls}`}>{record.gender || '—'}</span>
        </div>
        {publisher && <span className="bazi-card-publisher">@{publisher}</span>}
      </div>

      <MiniChart baziResult={record.baziResult || {}} />

      <div className="bazi-card-date">{formatDate(record.gregorianDate)}</div>

      {shownTags.length > 0 && (
        <div className="bazi-card-tags">
          {shownTags.map((t) => (
            <span className="bazi-card-tag" key={t}>{t}</span>
          ))}
          {extraTags > 0 && <span className="bazi-card-tag more">+{extraTags}</span>}
        </div>
      )}

      {actions.length > 0 && (
        <div className="bazi-card-menu" ref={menuRef}>
          <button
            type="button"
            className="bazi-card-menu-btn"
            aria-label="更多操作"
            onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
          >
            <MoreHorizontal size={18} />
          </button>
          {menuOpen && (
            <div className="bazi-card-popover" onClick={(e) => e.stopPropagation()}>
              {actions.map((a) => (
                <button
                  type="button"
                  key={a.key}
                  className={`bazi-card-popitem ${a.danger ? 'danger' : ''}`}
                  onClick={(e) => { e.stopPropagation(); close(); a.onClick && a.onClick(); }}
                >
                  {a.icon}
                  <span>{a.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BaziCard;
